import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckIcon from '@mui/icons-material/Check';
import { Box, FormControl, Grid, Tooltip, Typography } from '@mui/material/';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForgotPassword } from '@/api/mutations/useAuthMutations';
import { FormHelperText } from '@/components/FormHelperText/FormHelperText';
import { LogoSvg } from '@/components/Icons/LogoSvg';
import { Loading } from '@/components/Loading/Loading';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { paths } from '@/constants';
import { snackbarColor } from '@/enums';
import { useSnackbar } from '@/hooks';
import { isApiError } from '@/types';
import { validateForgotPassword } from '@/utils/validators';
import { ForgotButtonsGrid, InputLogin, InputTitleTypography, MailLoginTypography } from './LoginForm.styles';
import { LoginWrap } from './LoginWrap';

export const ForgotPasswordForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const validate = validateForgotPassword(t);
    const showSnackbar = useSnackbar();
    const [success, setSuccess] = useState(false);
    const { mutateAsync: forgotPassword, status } = useForgotPassword();

    const initialValues: { email: string } = {
        email: '',
    };

    const formik = useFormik({
        initialValues,
        validate,
        validateOnMount: true,
        isInitialValid: false,
        onSubmit: async values => {
            try {
                const { success } = await forgotPassword({ email: values.email });
                if (success) {
                    setSuccess(true);
                }
            } catch (error) {
                if (isApiError(error)) {
                    showSnackbar({
                        open: true,
                        message: error.data.message,
                        severity: snackbarColor.error,
                    });
                }
                formik.setSubmitting(false);
            }
        },
    });

    return (
        <LoginWrap>
            <Grid
                container
                onClick={() => {
                    navigate(paths.sign.index);
                }}
                sx={theme => ({
                    gap: '32px',
                    alignItems: 'center',
                    mb: '80px',
                    [theme.breakpoints.down('lg')]: { mb: '40px' },
                })}
            >
                <Tooltip placement="left" title={t('buttons.back')}>
                    <div>
                        <Button variant="actions">
                            <ArrowBackIcon color="primary" fontSize="small" />
                        </Button>
                    </div>
                </Tooltip>
                <Text variant="subtitle1" children={t('buttons.enter')} />
            </Grid>
            <Box
                display="inline-block"
                sx={theme => ({
                    mb: '60px',
                    [theme.breakpoints.down('lg')]: {
                        mb: '40px',
                        maxWidth: '205px',
                        svg: {
                            maxWidth: '100%',
                            height: 'auto',
                        },
                    },
                })}
            >
                <LogoSvg />
            </Box>
            <MailLoginTypography children={t(success ? 'helpers.email.check-your-email' : 'helpers.password.password-recovery')} />
            {success ? (
                <div className="flex flex-wrap w-full mb-[36px] gap-[5px]">
                    <Typography color={theme => theme.palette.primary.contrastText} children={t('helpers.email.send-letter')} />
                    <Typography color={theme => theme.custom.c11} children={`${formik.values.email},`} />
                    <Typography color={theme => theme.palette.primary.contrastText} children={t('helpers.email.reset-link')} />
                </div>
            ) : (
                <Typography mb="36px" color={theme => theme.custom.c4} children={t('helpers.email.forgot')} />
            )}

            {success ? (
                <div className="flex flex-wrap w-full gap-[24px]">
                    <Button
                        variant="contained"
                        size="large"
                        className="w-full max-w-[400px]"
                        onClick={() => navigate(paths.sign.index, { state: { from: location } })}
                    >
                        {t('buttons.return-login-page')}
                    </Button>
                    <ForgotButtonsGrid container onClick={() => setSuccess(false)}>
                        <AutorenewIcon color="secondary" sx={{ width: '16px', height: '16px' }} />
                        <Typography
                            children={t('buttons.resend-email')}
                            variant="h6"
                            sx={{
                                color: theme => theme.custom.c11,
                                textTransform: 'uppercase',
                            }}
                        />
                    </ForgotButtonsGrid>
                </div>
            ) : (
                <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
                    <FormControl sx={{ maxWidth: '400px' }}>
                        <InputTitleTypography children={t('helpers.password.your-mail')} />
                        <InputLogin
                            id="email"
                            name="email"
                            type="text"
                            placeholder={t('placeholders.enter-email')}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.email}
                            endAdornment={!formik.errors.email ? <CheckIcon color="success" /> : null}
                        />
                        <FormHelperText error={formik.errors.email} touched={formik.touched.email} />
                    </FormControl>

                    <Button
                        type="submit"
                        disabled={!formik.isValid || formik.isSubmitting}
                        variant="contained"
                        size="large"
                        className="mt-9 w-[400px]"
                    >
                        {status === 'pending' ? <Loading /> : t('buttons.restore')}
                    </Button>
                </form>
            )}
        </LoginWrap>
    );
};
